/**
 * Phase 33 §2 + Phase 34.B — Frontend Request Orchestrator.
 *
 * Single chokepoint for ALL frontend networking. Centralizes:
 *  • dedup of identical in-flight requests
 *  • concurrency cap (MAX_CONCURRENT)
 *  • TTL response cache for hot reads
 *  • per-request priority queueing (HIGH > MEDIUM > LOW)
 *  • timeout protection
 *  • retry with backoff
 *  • cancellation
 *  • visibility-aware throttling
 *
 * Usage:
 *   import orchestrator from '../lib/requestOrchestrator';
 *   const data = await orchestrator.request({
 *     key: `hydrate:${sym}`,
 *     url: `/market/hydrate/${sym}`,
 *     ttl: 30000,
 *     priority: 'HIGH',
 *   });
 *
 * Falls back to plain GET via api.get when only `url` is passed.
 */
import api from './api';
import browserProfile from './browserProfile';

const MAX_CONCURRENT     = 4;
const REQUEST_TIMEOUT_MS = 8000;
const RETRY_LIMIT        = 2;

const PRIORITY_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };

const inFlight   = new Map();    // key -> Promise
const cache      = new Map();    // key -> {ts, payload, ttl}
const queue      = [];           // [{order, runner, resolve, reject, key}]
let activeCount  = 0;

function _keyFor(method, url, body) {
  return `${method}:${url}:${body ? JSON.stringify(body) : ''}`;
}

function _hidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function _drain() {
  if (activeCount >= MAX_CONCURRENT) return;
  // sort by priority each drain — cheap, queues are short
  queue.sort((a, b) => a.order - b.order);
  while (activeCount < MAX_CONCURRENT && queue.length) {
    const job = queue.shift();
    activeCount++;
    job.runner()
      .then((r) => { activeCount--; inFlight.delete(job.key); job.resolve(r); _drain(); })
      .catch((e) => { activeCount--; inFlight.delete(job.key); job.reject(e); _drain(); });
  }
}

async function _withTimeoutAndRetry(runner, { timeout, retries }) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeout);
    try {
      const result = await runner(ctrl.signal);
      clearTimeout(timer);
      return result;
    } catch (e) {
      clearTimeout(timer);
      lastErr = e;
      if (attempt < retries) {
        // backoff: 200ms, 600ms
        await new Promise((r) => setTimeout(r, 200 * Math.pow(3, attempt)));
      }
    }
  }
  throw lastErr;
}

function _enqueue(key, runner, priority = 'MEDIUM') {
  if (inFlight.has(key)) return inFlight.get(key);
  const p = new Promise((resolve, reject) => {
    queue.push({
      key,
      order: PRIORITY_ORDER[priority] ?? 1,
      runner,
      resolve,
      reject,
    });
    _drain();
  });
  inFlight.set(key, p);
  return p;
}

function _checkCache(key) {
  const rec = cache.get(key);
  if (!rec) return null;
  if (Date.now() - rec.ts > rec.ttl) {
    cache.delete(key);
    return null;
  }
  return rec.payload;
}

function _saveCache(key, payload, ttl) {
  if (!ttl) return;
  cache.set(key, { ts: Date.now(), payload, ttl });
}

const orchestrator = {
  /**
   * Unified request entry-point.
   * opts: {key, url, method, body, ttl, priority, timeout, retries, force}
   */
  async request(opts) {
    const {
      url, method = 'GET', body, ttl = 0,
      priority = 'MEDIUM', force = false,
      timeout = REQUEST_TIMEOUT_MS, retries = RETRY_LIMIT,
    } = opts;
    const key = opts.key || _keyFor(method, url, body);

    if (!force && ttl > 0) {
      const cached = _checkCache(key);
      if (cached) return cached;
    }

    // Hidden-tab demotion: drop priority + skip retries to conserve.
    const effPriority = _hidden() ? 'LOW' : priority;
    const effRetries  = _hidden() ? 0 : retries;

    const runner = async () => {
      const result = await _withTimeoutAndRetry(async () => {
        const cfg = {};
        if (method === 'GET') {
          const r = await api.get(url, cfg);
          return r.data;
        }
        const r = await api.request({ url, method, data: body, ...cfg });
        return r.data;
      }, { timeout, retries: effRetries });
      _saveCache(key, result, ttl);
      return result;
    };

    return _enqueue(key, runner, effPriority);
  },

  // Convenience wrappers retained from earlier phase.
  get(url, config = {}) {
    return this.request({ url, method: 'GET', priority: 'MEDIUM', ...config });
  },
  post(url, body, config = {}) {
    return this.request({ url, method: 'POST', body, priority: 'MEDIUM', ...config });
  },

  cancel(method, url, body) {
    const key = _keyFor(method.toUpperCase(), url, body);
    inFlight.delete(key);
    cache.delete(key);
  },

  invalidate(prefix) {
    for (const k of cache.keys()) {
      if (k.includes(prefix)) cache.delete(k);
    }
  },

  stats() {
    return {
      active: activeCount,
      queued: queue.length,
      in_flight: inFlight.size,
      cache_size: cache.size,
      browser: browserProfile.family,
    };
  },
};

export default orchestrator;
